/**
 * 🪝 API Hooks - TanStack Query Integration
 * 
 * Hooks personalizados que conectam nossa API REST com React Query
 * Fornece cache automático, sincronização em background e gerenciamento de estado
 * 
 * Funcionalidades:
 * - Cache inteligente de dados
 * - Sincronização em background 
 * - Loading e error states automáticos
 * - Invalidação automática de queries relacionadas
 * - Otimistic updates
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import api, {
  type EventFilters,
  type ParticipantFilters,
  type UserFilters,
  type MeetingFilters,
  type CreateEventData,
  type UpdateEventData,
  type CreateUserData,
  type UpdateUserData,
  type CreateParticipantData,
  type UpdateParticipantData,
  type CreateMeetingData,
  type UpdateMeetingData,
} from '../services/api';

// ========================================
// QUERY KEYS - Chaves padronizadas
// ========================================

export const queryKeys = {
  // Events
  events: ['events'] as const,
  eventList: (filters?: EventFilters) => ['events', 'list', filters] as const,
  eventDetail: (id: string) => ['events', 'detail', id] as const,
  eventStats: (id: string) => ['events', 'stats', id] as const,

  // Users  
  users: ['users'] as const,
  userList: (filters?: UserFilters) => ['users', 'list', filters] as const,
  userDetail: (id: string) => ['users', 'detail', id] as const,
  userProfile: ['users', 'profile'] as const,

  // Participants
  participants: ['participants'] as const,
  participantList: (filters?: ParticipantFilters) => ['participants', 'list', filters] as const,
  participantDetail: (id: string) => ['participants', 'detail', id] as const,

  // Meetings
  meetings: ['meetings'] as const,
  meetingList: (filters?: MeetingFilters) => ['meetings', 'list', filters] as const,
  meetingDetail: (id: string) => ['meetings', 'detail', id] as const,

  // System
  health: ['system', 'health'] as const,
  info: ['system', 'info'] as const,
};

// ========================================
// 🎯 EVENTS HOOKS
// ========================================

/**
 * Hook para listar eventos
 */
export function useEvents(filters?: EventFilters) {
  return useQuery({
    queryKey: queryKeys.eventList(filters),
    queryFn: () => api.events.getAll(filters),
    select: (response) => response.data, // Extrair só os dados da APIResponse
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
}

/**
 * Hook para buscar evento específico
 */
export function useEvent(id: string) {
  return useQuery({
    queryKey: queryKeys.eventDetail(id),
    queryFn: () => api.events.getById(id),
    select: (response) => response.data,
    enabled: !!id, // Só executa se ID estiver presente
  });
}

/**
 * Hook para estatísticas do evento
 */
export function useEventStats(id: string) {
  return useQuery({
    queryKey: queryKeys.eventStats(id),
    queryFn: () => api.events.getStats(id),
    select: (response) => response.data,
    enabled: !!id,
  });
}

/**
 * Hook para criar evento
 */
export function useCreateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateEventData) => api.events.create(data),
    onSuccess: (response) => {
      // Invalidar lista de eventos
      queryClient.invalidateQueries({ queryKey: queryKeys.events });
      toast.success('Evento criado com sucesso!');
      return response.data;
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar evento: ${error.message}`);
    },
  });
}

/**
 * Hook para atualizar evento
 */
export function useUpdateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEventData }) => 
      api.events.update(id, data),
    onSuccess: (response, variables) => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: queryKeys.events });
      queryClient.invalidateQueries({ queryKey: queryKeys.eventDetail(variables.id) });
      toast.success('Evento atualizado com sucesso!');
      return response.data;
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar evento: ${error.message}`);
    },
  });
}

/**
 * Hook para deletar evento
 */
export function useDeleteEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.events.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.events });
      toast.success('Evento removido com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao remover evento: ${error.message}`);
    },
  });
}

// ========================================
// 👥 USERS HOOKS
// ========================================

/**
 * Hook para listar usuários
 */
export function useUsers(filters?: UserFilters) {
  return useQuery({
    queryKey: queryKeys.userList(filters),
    queryFn: () => api.users.getAll(filters),
    select: (response) => response.data,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook para buscar usuário específico
 */
export function useUser(id: string) {
  return useQuery({
    queryKey: queryKeys.userDetail(id),
    queryFn: () => api.users.getById(id),
    select: (response) => response.data,
    enabled: !!id,
  });
}

/**
 * Hook para perfil do usuário atual
 */
export function useUserProfile() {
  return useQuery({
    queryKey: queryKeys.userProfile,
    queryFn: () => api.users.getProfile(),
    select: (response) => response.data,
    staleTime: 2 * 60 * 1000, // 2 minutos para perfil
  });
}

/**
 * Hook para criar usuário
 */
export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUserData) => api.users.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users });
      toast.success('Usuário criado com sucesso!');
      return response.data;
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar usuário: ${error.message}`);
    },
  });
}

/**
 * Hook para atualizar usuário
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserData }) => 
      api.users.update(id, data),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users });
      queryClient.invalidateQueries({ queryKey: queryKeys.userDetail(variables.id) });
      toast.success('Usuário atualizado com sucesso!');
      return response.data;
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar usuário: ${error.message}`);
    },
  });
}

/**
 * Hook para atualizar perfil
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateUserData) => api.users.updateProfile(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userProfile });
      queryClient.invalidateQueries({ queryKey: queryKeys.users });
      toast.success('Perfil atualizado com sucesso!');
      return response.data;
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar perfil: ${error.message}`);
    },
  });
}

/**
 * Hook para deletar usuário
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.users.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users });
      toast.success('Usuário removido com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao remover usuário: ${error.message}`);
    },
  });
}

// ========================================
// 🏢 PARTICIPANTS HOOKS
// ========================================

/**
 * Hook para listar participantes
 */
export function useParticipants(filters?: ParticipantFilters) {
  return useQuery({
    queryKey: queryKeys.participantList(filters),
    queryFn: () => api.participants.getAll(filters),
    select: (response) => response.data,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook para buscar participante específico
 */
export function useParticipant(id: string) {
  return useQuery({
    queryKey: queryKeys.participantDetail(id),
    queryFn: () => api.participants.getById(id),
    select: (response) => response.data,
    enabled: !!id,
  });
}

/**
 * Hook para criar participante
 */
export function useCreateParticipant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateParticipantData) => api.participants.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.participants });
      // Também invalidar eventos relacionados
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.eventDetail(response.data.eventId) 
      });
      toast.success('Participante adicionado com sucesso!');
      return response.data;
    },
    onError: (error: Error) => {
      toast.error(`Erro ao adicionar participante: ${error.message}`);
    },
  });
}

/**
 * Hook para atualizar participante
 */
export function useUpdateParticipant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateParticipantData }) => 
      api.participants.update(id, data),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.participants });
      queryClient.invalidateQueries({ queryKey: queryKeys.participantDetail(variables.id) });
      toast.success('Participante atualizado com sucesso!');
      return response.data;
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar participante: ${error.message}`);
    },
  });
}

/**
 * Hook para deletar participante
 */
export function useDeleteParticipant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.participants.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.participants });
      toast.success('Participante removido com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao remover participante: ${error.message}`);
    },
  });
}

/**
 * Hook para adicionar representante
 */
export function useAddRepresentative() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      api.participants.addRepresentative(id, data),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.participants });
      queryClient.invalidateQueries({ queryKey: queryKeys.participantDetail(variables.id) });
      toast.success('Representante adicionado com sucesso!');
      return response.data;
    },
    onError: (error: Error) => {
      toast.error(`Erro ao adicionar representante: ${error.message}`);
    },
  });
}

// ========================================
// 🤝 MEETINGS HOOKS
// ========================================

/**
 * Hook para listar reuniões
 */
export function useMeetings(filters?: MeetingFilters) {
  return useQuery({
    queryKey: queryKeys.meetingList(filters),
    queryFn: () => api.meetings.getAll(filters),
    select: (response) => response.data,
    staleTime: 2 * 60 * 1000, // 2 minutos para reuniões (mais dinâmico)
  });
}

/**
 * Hook para buscar reunião específica
 */
export function useMeeting(id: string) {
  return useQuery({
    queryKey: queryKeys.meetingDetail(id),
    queryFn: () => api.meetings.getById(id),
    select: (response) => response.data,
    enabled: !!id,
  });
}

/**
 * Hook para criar reunião
 */
export function useCreateMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMeetingData) => api.meetings.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings });
      // Invalidar evento relacionado
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.eventDetail(response.data.eventId) 
      });
      toast.success('Reunião agendada com sucesso!');
      return response.data;
    },
    onError: (error: Error) => {
      toast.error(`Erro ao agendar reunião: ${error.message}`);
    },
  });
}

/**
 * Hook para atualizar reunião
 */
export function useUpdateMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMeetingData }) => 
      api.meetings.update(id, data),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings });
      queryClient.invalidateQueries({ queryKey: queryKeys.meetingDetail(variables.id) });
      toast.success('Reunião atualizada com sucesso!');
      return response.data;
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar reunião: ${error.message}`);
    },
  });
}

/**
 * Hook para confirmar reunião
 */
export function useConfirmMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.meetings.confirm(id),
    onSuccess: (response, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings });
      queryClient.invalidateQueries({ queryKey: queryKeys.meetingDetail(id) });
      toast.success('Participação confirmada!');
      return response.data;
    },
    onError: (error: Error) => {
      toast.error(`Erro ao confirmar reunião: ${error.message}`);
    },
  });
}

/**
 * Hook para cancelar reunião
 */
export function useCancelMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.meetings.cancel(id),
    onSuccess: (response, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings });
      queryClient.invalidateQueries({ queryKey: queryKeys.meetingDetail(id) });
      toast.success('Reunião cancelada!');
      return response.data;
    },
    onError: (error: Error) => {
      toast.error(`Erro ao cancelar reunião: ${error.message}`);
    },
  });
}

/**
 * Hook para deletar reunião
 */
export function useDeleteMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.meetings.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings });
      toast.success('Reunião removida com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao remover reunião: ${error.message}`);
    },
  });
}

// ========================================
// 🩺 SYSTEM HOOKS
// ========================================

/**
 * Hook para health check
 */
export function useHealth() {
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: () => api.system.health(),
    staleTime: 30 * 1000, // 30 segundos
    refetchInterval: 60 * 1000, // Refetch a cada minuto
  });
}

/**
 * Hook para informações da API
 */
export function useApiInfo() {
  return useQuery({
    queryKey: queryKeys.info,
    queryFn: () => api.system.info(),
    select: (response) => response.data,
    staleTime: 10 * 60 * 1000, // 10 minutos (informação estática)
  });
}

// ========================================
// 🔧 UTILITY HOOKS
// ========================================

/**
 * Hook para invalidar todas as queries relacionadas a um evento
 */
export function useInvalidateEventQueries() {
  const queryClient = useQueryClient();

  return (eventId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.events });
    queryClient.invalidateQueries({ queryKey: queryKeys.eventDetail(eventId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.eventStats(eventId) });
    queryClient.invalidateQueries({ 
      queryKey: queryKeys.participantList({ eventId }) 
    });
    queryClient.invalidateQueries({ 
      queryKey: queryKeys.meetingList({ eventId }) 
    });
  };
}

/**
 * Hook para limpar cache específico
 */
export function useClearCache() {
  const queryClient = useQueryClient();

  return {
    clearAll: () => queryClient.clear(),
    clearEvents: () => queryClient.removeQueries({ queryKey: queryKeys.events }),
    clearUsers: () => queryClient.removeQueries({ queryKey: queryKeys.users }),
    clearParticipants: () => queryClient.removeQueries({ queryKey: queryKeys.participants }),
    clearMeetings: () => queryClient.removeQueries({ queryKey: queryKeys.meetings }),
  };
}

/**
 * Hook para status de conexão com a API
 */
export function useApiStatus() {
  const { data: health, isError, isLoading } = useHealth();
  
  return {
    isConnected: !isError && health?.ok,
    isLoading,
    health,
  };
}

// ========================================
// EXPORT ALL HOOKS
// ========================================

export default {
  // Events
  useEvents,
  useEvent,
  useEventStats,
  useCreateEvent,
  useUpdateEvent,
  useDeleteEvent,
  
  // Users
  useUsers,
  useUser,
  useUserProfile,
  useCreateUser,
  useUpdateUser,
  useUpdateProfile,
  useDeleteUser,
  
  // Participants  
  useParticipants,
  useParticipant,
  useCreateParticipant,
  useUpdateParticipant,
  useDeleteParticipant,
  useAddRepresentative,
  
  // Meetings
  useMeetings,
  useMeeting,
  useCreateMeeting,
  useUpdateMeeting,
  useConfirmMeeting,
  useCancelMeeting,
  useDeleteMeeting,
  
  // System
  useHealth,
  useApiInfo,
  useApiStatus,
  
  // Utilities
  useInvalidateEventQueries,
  useClearCache,
  queryKeys,
};